import { useState } from 'react';
import { useStripe, useElements, CardNumberElement, CardExpiryElement, CardCvcElement } from '@stripe/react-stripe-js';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { CreditCard, Lock, Shield } from 'lucide-react';

interface TraditionalPaymentFormProps {
  clientSecret: string;
  amount?: number;
  currency?: string;
  onSuccess: (paymentIntentId?: string) => void;
  onError: (error: string) => void;
}

export const TraditionalPaymentForm = ({ 
  clientSecret, 
  amount, 
  currency = 'USD',
  onSuccess, 
  onError 
}: TraditionalPaymentFormProps) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [billingName, setBillingName] = useState('');
  const [billingEmail, setBillingEmail] = useState('');

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!stripe || !elements) {
      onError('Stripe has not loaded yet. Please try again.');
      return;
    }

    setLoading(true);

    try {
      const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: elements.getElement(CardNumberElement)!,
          billing_details: {
            name: billingName,
            email: billingEmail,
          },
        },
      });

      if (error) {
        onError(error.message || 'Payment failed. Please try again.');
      } else if (paymentIntent && paymentIntent.status === 'succeeded') {
        onSuccess(paymentIntent.id);
      } else {
        onError('Payment was not completed. Please try again.');
      }
    } catch (err) {
      onError('An unexpected error occurred during payment processing.');
    } finally {
      setLoading(false);
    }
  };

  const formatAmount = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(amount / 100);
  };

  return (
    <Card className="w-full max-w-md mx-auto">
      <CardHeader className="text-center">
        <div className="flex justify-center mb-2">
          <div className="p-2 bg-green-100 rounded-full">
            <Shield className="h-6 w-6 text-green-600" />
          </div>
        </div>
        <CardTitle>Secure Payment</CardTitle>
        {amount && (
          <p className="text-2xl font-bold text-green-600">
            {formatAmount(amount, currency)}
          </p>
        )}
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Billing Information */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-gray-700">Billing Information</h3>
            <div className="space-y-3">
              <div>
                <Label htmlFor="billing-name">Full Name</Label>
                <Input
                  id="billing-name"
                  type="text"
                  value={billingName}
                  onChange={(e) => setBillingName(e.target.value)}
                  placeholder="John Doe"
                  required
                />
              </div>
              <div>
                <Label htmlFor="billing-email">Email</Label>
                <Input
                  id="billing-email"
                  type="email"
                  value={billingEmail}
                  onChange={(e) => setBillingEmail(e.target.value)}
                  placeholder="john@example.com"
                  required
                />
              </div>
            </div>
          </div>

          <Separator />

          {/* Card Information */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-gray-700">Card Information</h3>
            <div className="space-y-3">
              <div>
                <Label htmlFor="card-number">Card Number</Label>
                <div className="relative">
                  <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                    <CreditCard className="h-4 w-4 text-gray-400" />
                  </div>
                  <div className="pl-10 pr-3 py-2 border rounded-md">
                    <CardNumberElement
                      id="card-number"
                      options={{
                        style: {
                          base: {
                            fontSize: '16px',
                            color: '#374151',
                            '::placeholder': {
                              color: '#9CA3AF',
                            },
                          },
                          invalid: {
                            color: '#EF4444',
                          },
                        },
                        placeholder: '1234 1234 1234 1234',
                      }}
                    />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="card-expiry">Expiry Date</Label>
                  <div className="border rounded-md p-2">
                    <CardExpiryElement
                      id="card-expiry"
                      options={{
                        style: {
                          base: {
                            fontSize: '16px',
                            color: '#374151',
                            '::placeholder': {
                              color: '#9CA3AF',
                            },
                          },
                          invalid: {
                            color: '#EF4444',
                          },
                        },
                        placeholder: 'MM/YY',
                      }}
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="card-cvc">CVC</Label>
                  <div className="border rounded-md p-2">
                    <CardCvcElement
                      id="card-cvc"
                      options={{
                        style: {
                          base: {
                            fontSize: '16px',
                            color: '#374151',
                            '::placeholder': {
                              color: '#9CA3AF',
                            },
                          },
                          invalid: {
                            color: '#EF4444',
                          },
                        },
                        placeholder: '123',
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Security Notice */}
          <div className="flex items-center gap-2 text-sm text-gray-600 bg-gray-50 p-3 rounded-md">
            <Lock className="h-4 w-4" />
            <span>Your payment information is encrypted and secure</span>
          </div>

          <Button
            type="submit"
            className="w-full bg-recallio-green hover:bg-recallio-green/90"
            disabled={!stripe || loading}
          >
            {loading ? 'Processing Payment...' : 'Complete Payment'}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}; 